/**
 * Catalog of (entity, action) pairs the Access Manager can assign.
 *
 * Entities are grouped by domain — the UI uses these groups as section
 * headings in the dual-list. Actions match the backend enum on
 * `user_permissions.action` and `role_permissions.action`.
 *
 * `full` is a shorthand — backend `getUserPermissions()` expands it into
 * every action. We still surface it as a selectable atom so users can
 * pick "full access for this entity" without ticking every box.
 */

export const ACTIONS = ['show', 'list', 'edit', 'create', 'delete', 'full', 'deploy'] as const;
export type Action = (typeof ACTIONS)[number];

export interface EntityGroup {
  label: string;
  entities: string[];
  /**
   * Apps this group is relevant to. Empty/undefined = always shown.
   * Used by `PermissionDualList` to scope the editor by current session app.
   */
  apps?: string[];
}

export const ENTITY_GROUPS: EntityGroup[] = [
  {
    label: 'Access Manager (Core)',
    entities: ['User', 'Role', 'UserPermission', 'RolePermission', 'ExternalAccessGrant'],
    // shown in every app
  },
  {
    // Notifications is a core feature: any user can receive + mark read without
    // a permission, but composing/sending requires Notification:create.
    label: 'Notifications',
    entities: ['Notification'],
  },
  {
    // Multi-tenant infrastructure used by every app — leaving `apps` empty
    // means the group is always visible regardless of the current scope.
    // Without this, an Access Manager working in xotski/xamplin/etc. couldn't
    // grant `CoCompany:show` / `CoBrand:show` and members would 403 on home.
    label: 'Companies & Brands',
    entities: ['CoCompany', 'CoBrand', 'CoCompanyAccess', 'CoBrandKit'],
  },
  {
    label: 'Xotski',
    entities: [
      'XkCompany',
      'XkStock',
      'XkEvent',
      'XkEventGroup',
      'XkStation',
      'XkEventStockStation',
      'XkDashboard',
    ],
    apps: ['xotski'],
  },
  {
    label: 'Xamplin',
    entities: ['XpProject', 'XpEvents', 'XpEventGroup', 'XpStation', 'XpReporter'],
    apps: ['xamplin'],
  },
  {
    label: 'Xurvey',
    entities: ['XySurvey', 'XyStep', 'XyComponent'],
    apps: ['xurvey'],
  },
  {
    label: 'DiD',
    entities: ['DidStation', 'DidStock', 'DidEvent', 'DidEventGroup'],
    apps: ['did'],
  },
  {
    label: 'Ecommerce',
    entities: ['EcPlan', 'EcInvoicePlan'],
    apps: ['console'],
  },
];

/**
 * Guess which app owns an entity from its name prefix. Used to filter the
 * "Other / Legacy" fallback so the dialog stays scoped to the current app.
 */
export function entityAppGuess(entity: string): string {
  if (entity.startsWith('Xk')) return 'xotski';
  if (entity.startsWith('Xp')) return 'xamplin';
  if (entity.startsWith('Xy')) return 'xurvey';
  if (entity.startsWith('Did')) return 'did';
  if (entity.startsWith('Co')) return 'console';
  if (entity.startsWith('Ec')) return 'console';
  return 'core'; // User, Role, UserPermission, RolePermission, ExternalAccessGrant, Notification
}

/**
 * True if an entity is relevant to a given app. `core` and `console` entities
 * (User/Role/CoCompany/CoBrand/...) match every app because they're shared
 * multi-tenant infrastructure used by all of them.
 */
export function entityMatchesApp(entity: string, app: string | null | undefined): boolean {
  if (!app) return true;
  const guess = entityAppGuess(entity);
  return guess === 'core' || guess === 'console' || guess === app;
}

/**
 * Flat list of every (entity, action) the catalog exposes.
 * When `app` is provided, only entities for that app + core are included.
 */
export function flatCatalog(app?: string | null): Array<{ entity: string; action: Action; group: string }> {
  const out: Array<{ entity: string; action: Action; group: string }> = [];
  for (const g of ENTITY_GROUPS) {
    if (app && g.apps && !g.apps.includes(app)) continue;
    for (const entity of g.entities) {
      for (const action of ACTIONS) {
        out.push({ entity, action, group: g.label });
      }
    }
  }
  return out;
}

export function permissionId(entity: string, action: string): string {
  return `${entity}::${action}`;
}

export function parsePermissionId(id: string): { entity: string; action: Action } {
  const [entity, action] = id.split('::');
  return { entity, action: action as Action };
}
